@file:Suppress("UNCHECKED_CAST_TO_EXTERNAL_INTERFACE")

package org.example.project.dashboard

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import kotlinx.browser.window
import kotlinx.coroutines.launch
import kotlinx.serialization.decodeFromString
import kotlinx.serialization.json.Json
import org.example.project.enginechat.canSeeKontaktKalender
import org.example.project.enginechat.canSeeKontaktProfil
import org.example.project.enginechat.chatIdentityUserId
import org.example.project.enginechat.globalChatUserId
import org.example.project.enginechat.isCurrentChatUser
import org.example.project.enginechat.shortUserDisplayName
import org.example.project.enginechat.userChatId
import org.example.project.enginechat.userDisplayName
import org.example.project.enginekartei.EngineKarteiField
import org.example.project.enginekartei.EngineKarteiScreen
import org.example.project.manager.LumaticaApiManager
import org.example.project.manager.NodeScope
import org.example.project.registrierung.RegistrierungApi
import org.example.project.registrierung.TrustedDevice
import org.example.project.registrierung.TrustedDevicesApi
import org.example.project.service.ChatPopupService
import org.example.project.service.GlobalManager
import org.example.project.service.KontaktKalenderPopupService
import org.example.project.service.LoginService
import org.example.project.service.LoginUserApi
import org.example.project.settings.AppDropdown
import org.example.project.settings.LumaticaButton
import org.example.project.settings.LumaticaButtonVariant
import org.example.project.utils.All
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

private val meinProfilAnchorFields = listOf(
    EngineKarteiField("vorname", "Vorname", editable = false),
    EngineKarteiField("nachname", "Nachname", editable = false)
)

private val meinProfilPageFields = listOf(
    EngineKarteiField("email", "E-Mail", editable = false),
    EngineKarteiField("position", "Position", editable = false),
    EngineKarteiField("firma", "Firma", editable = false),
    EngineKarteiField("plz", "PLZ", editable = false),
    EngineKarteiField("strasse", "Straße", editable = false),
    EngineKarteiField("hausnummer", "Hausnummer", editable = false)
)

val dashboardProfilKarteiPages = listOf(
    All(id = "page_1", bezeichnung = "Profil"),
    All(id = "page_2", bezeichnung = "Kontakte")
)

private val kontakteJson = Json {
    ignoreUnknownKeys = true
    encodeDefaults = true
}

private fun kontakteApiPart(value: String): String =
    js("encodeURIComponent")(if (value.isBlank()) "~" else value) as String

private fun dashboardProfilTextWidthPx(text: String): Int =
    text.sumOf { char ->
        when {
            char.isWhitespace() -> 4.0
            char.isUpperCase() -> 8.0
            char in "ilI.,:;|!" -> 3.8
            char in "mwMW" -> 9.8
            else -> 7.0
        }
    }.toInt()

private fun dashboardProfilDropdownWidthPx(label: String, placeholder: String): Int =
    (dashboardProfilTextWidthPx(label.ifBlank { placeholder }) + 58).coerceIn(112, 230)

private suspend fun loadMeinProfilContactUsers(firmenId: String): List<All> {
    if (firmenId.isBlank()) return emptyList()
    val raw = runCatching {
        LumaticaApiManager.call("kontakte/users/${kontakteApiPart(firmenId)}")
    }.getOrDefault("")
    if (raw.isBlank()) return emptyList()
    return runCatching { kontakteJson.decodeFromString<List<All>>(raw) }.getOrDefault(emptyList())
}

private suspend fun loadMeinProfilGlobalCompanies(currentFirmenId: String): List<All> {
    val raw = runCatching {
        LumaticaApiManager.call("kontakte/global/${kontakteApiPart(currentFirmenId)}")
    }.getOrDefault("")
    if (raw.isBlank()) return emptyList()
    return runCatching { kontakteJson.decodeFromString<List<All>>(raw) }.getOrDefault(emptyList())
}

private suspend fun loadMeinProfilContactCompanies(currentUserId: String): List<All> {
    val currentUser = LoginService.getUser()
    val currentFirmenId = currentUser?.firmenId.orEmpty().trim()
    val currentChatUserId = globalChatUserId(currentFirmenId, currentUser?.id.orEmpty())
    val internalCompanies = if (currentFirmenId.isBlank()) {
        emptyList()
    } else {
        val internalUsers = loadMeinProfilContactUsers(currentFirmenId)
            .asSequence()
            .filter { it.id.isNotBlank() }
            .filterNot { isCurrentChatUser(it, currentFirmenId, currentUserId, currentChatUserId) }
            .filter { it.firmenId == currentFirmenId }
            .filter { canSeeKontaktProfil(currentFirmenId, it) }
            .map { it.copy(id = globalChatUserId(it.firmenId, it.id)) }
            .sortedBy { userDisplayName(it).lowercase() }
            .toList()
        val companyLabel = internalUsers.firstOrNull()?.firma
            .orEmpty()
            .ifBlank { internalUsers.firstOrNull()?.kunde.orEmpty() }
            .ifBlank { currentUser?.kunde.orEmpty() }
        listOf(
            All(
                firmenId = currentFirmenId,
                firma = companyLabel,
                kunde = companyLabel,
                columnSettings = internalUsers
            )
        )
    }

    val externalCompanies = loadMeinProfilGlobalCompanies(currentFirmenId).mapNotNull { company ->
        val companyId = company.firmenId.trim()
        if (companyId.isBlank() || companyId == currentFirmenId) return@mapNotNull null
        val users = company.columnSettings
            .filterNot { isCurrentChatUser(it, currentFirmenId, currentUserId, currentChatUserId) }
            .sortedBy { userDisplayName(it).lowercase() }
        if (users.isEmpty()) null else company.copy(columnSettings = users)
    }

    return (internalCompanies + externalCompanies).filter { it.columnSettings.isNotEmpty() }
}

private suspend fun loadKontaktUser(firmenId: String, userId: String): All? {
    val companyId = firmenId.trim()
    val localUserId = chatIdentityUserId(userId)
    if (companyId.isBlank() || localUserId.isBlank()) return null
    val raw = runCatching {
        LumaticaApiManager.call("userverwaltung/company-user/${kontakteApiPart(companyId)}/${kontakteApiPart(localUserId)}")
    }.getOrDefault("")
    if (raw.isBlank() || raw == "null") return null
    return runCatching { kontakteJson.decodeFromString(All.AllNames.serializer(), raw) }.getOrNull()
}

private suspend fun loadKontaktFirma(firmenId: String): All? {
    val companyId = firmenId.trim()
    if (companyId.isBlank()) return null
    return runCatching {
        RegistrierungApi.loadCompanies().firstOrNull { it.firmenId == companyId || it.id == companyId }
    }.getOrNull()
}

private fun kontaktCompaniesLabel(company: All): String =
    company.firma.ifBlank { company.kunde.ifBlank { company.firmenId.ifBlank { company.id.ifBlank { "Firma" } } } }

private fun kontaktUsers(company: All): List<All> =
    company.columnSettings.sortedBy { userDisplayName(it).lowercase() }

private fun kontaktUserState(user: All, company: All?): All {
    return user.copy(
        id = globalChatUserId(user.firmenId, user.id),
        name = userDisplayName(user),
        vorname = user.vorname.trim(),
        nachname = user.nachname.trim(),
        email = user.email.trim(),
        position = user.position.ifBlank { user.abteilung }.trim(),
        firma = company?.firma?.ifBlank { company.kunde }.orEmpty().ifBlank { user.firma.ifBlank { user.kunde } },
        plz = company?.plz.orEmpty(),
        strasse = company?.strasse.orEmpty(),
        hausnummer = company?.hausnummer.orEmpty(),
        terminkalenderOnline = user.terminkalenderOnline,
        terminkalenderIntern = user.terminkalenderIntern,
        profilOnline = user.profilOnline,
        profilIntern = user.profilIntern,
        startbildschirm = user.startbildschirm
    )
}

@Composable
fun DashboardProfil() {
    val scope = rememberCoroutineScope()
    val art = when (window.location.port) {
        "2000" -> NodeScope.NET
        else -> if (GlobalManager.currentFirmenId.isBlank()) NodeScope.SYS else NodeScope.CUS
    }
    val node = when (art) {
        NodeScope.NET -> DashboardNode.NET
        NodeScope.CUS -> DashboardNode.CUS
        else -> DashboardNode.SYS
    }
    val currentUserId = LoginService.getId().trim()
    val currentLoginUser = LoginService.getUser()
    val currentFirmenId = currentLoginUser?.firmenId.orEmpty().trim()
    val userVersion = LoginUserApi.version()
    var contactCompanies by remember { mutableStateOf<List<All>>(emptyList()) }
    var ownUserDetail by remember { mutableStateOf<All?>(null) }
    var ownCompanyDetail by remember { mutableStateOf<All?>(null) }
    var selectedCompanyId by remember { mutableStateOf("") }
    var selectedUserId by remember { mutableStateOf("") }
    var selectedUserDetail by remember { mutableStateOf<All?>(null) }
    var selectedCompanyDetail by remember { mutableStateOf<All?>(null) }
    var showPasswordOverlay by remember { mutableStateOf(false) }
    var showDashboardSettings by remember { mutableStateOf(false) }
    var showTrustedDevices by remember { mutableStateOf(false) }
    var trustedDevices by remember { mutableStateOf<List<TrustedDevice>>(emptyList()) }
    var loadingTrustedDevices by remember { mutableStateOf(false) }
    var selectedTrustedDeviceIds by remember { mutableStateOf<Set<String>>(emptySet()) }
    var editingTrustedDeviceId by remember { mutableStateOf("") }
    var editingTrustedDeviceLabel by remember { mutableStateOf("") }

    fun reloadTrustedDevices() {
        loadingTrustedDevices = true
        scope.launch {
            trustedDevices = TrustedDevicesApi.load("desktopregistrierungscreen")
            val ids = trustedDevices.map { it.id }.toSet()
            selectedTrustedDeviceIds = selectedTrustedDeviceIds.filter { it in ids }.toSet()
            if (editingTrustedDeviceId.isNotBlank() && editingTrustedDeviceId !in ids) editingTrustedDeviceId = ""
            loadingTrustedDevices = false
        }
    }

    LaunchedEffect(art, currentUserId, userVersion) {
        contactCompanies = loadMeinProfilContactCompanies(currentUserId)
        selectedCompanyId = currentFirmenId
        selectedUserId = ""
        selectedUserDetail = null
        selectedCompanyDetail = null
    }

    LaunchedEffect(currentFirmenId, currentUserId, userVersion) {
        if (currentFirmenId.isBlank() || currentUserId.isBlank()) {
            ownUserDetail = null
            ownCompanyDetail = null
            return@LaunchedEffect
        }
        ownCompanyDetail = loadKontaktFirma(currentFirmenId)
        ownUserDetail = loadKontaktUser(currentFirmenId, currentUserId)
    }

    val companyOptions = remember(contactCompanies) {
        contactCompanies.map { company ->
            val companyId = company.firmenId.ifBlank { company.id }
            All(key = companyId, label = kontaktCompaniesLabel(company))
        }
    }
    val selectedCompany = remember(contactCompanies, selectedCompanyId) {
        contactCompanies.firstOrNull { (it.firmenId.ifBlank { it.id }) == selectedCompanyId }
    }
    val usersForCompany = remember(selectedCompany) { selectedCompany?.let(::kontaktUsers).orEmpty() }

    LaunchedEffect(selectedCompanyId, usersForCompany) {
        if (usersForCompany.none { userChatId(it) == selectedUserId }) {
            selectedUserId = usersForCompany.firstOrNull()?.let(::userChatId).orEmpty()
        }
    }

    val selectedUser = remember(usersForCompany, selectedUserId) {
        usersForCompany.firstOrNull { userChatId(it) == selectedUserId }
    }

    LaunchedEffect(selectedCompanyId, selectedUserId, userVersion) {
        if (selectedCompanyId.isBlank() || selectedUserId.isBlank()) {
            selectedUserDetail = null
            selectedCompanyDetail = null
            return@LaunchedEffect
        }
        selectedCompanyDetail = loadKontaktFirma(selectedCompanyId)
        selectedUserDetail = loadKontaktUser(selectedCompanyId, selectedUserId)
    }

    val selectedState = remember(selectedUserDetail, selectedCompanyDetail) {
        selectedUserDetail?.let { kontaktUserState(it, selectedCompanyDetail) }
    }
    val ownState = remember(ownUserDetail, ownCompanyDetail, currentLoginUser, currentFirmenId) {
        ownUserDetail?.let { kontaktUserState(it, ownCompanyDetail) }
            ?: currentLoginUser?.copy(
                id = globalChatUserId(currentFirmenId, currentLoginUser.id),
                name = userDisplayName(currentLoginUser),
                vorname = currentLoginUser.vorname.trim(),
                nachname = currentLoginUser.nachname.trim(),
                email = currentLoginUser.email.trim(),
                position = currentLoginUser.position.ifBlank { currentLoginUser.abteilung }.trim(),
                firma = ownCompanyDetail?.firma?.ifBlank { ownCompanyDetail?.kunde }.orEmpty()
                    .ifBlank { currentLoginUser.firma.ifBlank { currentLoginUser.kunde } },
                plz = ownCompanyDetail?.plz.orEmpty(),
                strasse = ownCompanyDetail?.strasse.orEmpty(),
                hausnummer = ownCompanyDetail?.hausnummer.orEmpty(),
                terminkalenderOnline = currentLoginUser.terminkalenderOnline,
                terminkalenderIntern = currentLoginUser.terminkalenderIntern,
                profilOnline = currentLoginUser.profilOnline,
                profilIntern = currentLoginUser.profilIntern
            )
    }

    val activeEntities = remember(ownState) { ownState?.let { listOf(it) }.orEmpty() }
    val canAct = selectedState != null && selectedUser != null
    val canOpenCalendar = selectedState?.let { canSeeKontaktKalender(currentFirmenId, it) } == true
    val ownKontaktUserId = currentUserId
    val ownKontaktFirmenId = currentFirmenId
    val selectedKontaktUserId = selectedUser?.let { chatIdentityUserId(it.id) }.orEmpty()
    val selectedKontaktFirmenId = selectedUser?.firmenId?.trim().orEmpty()
    val eigenerKalenderPopupAktiv = KontaktKalenderPopupService.getPopups(node).any { popup ->
        popup.userName == ownKontaktUserId && popup.firmenId.trim() == ownKontaktFirmenId
    }
    val kalenderPopupAktiv = selectedUser != null && KontaktKalenderPopupService.getPopups(node).any { popup ->
        popup.userName == selectedKontaktUserId && popup.firmenId.trim() == selectedKontaktFirmenId
    }
    val chatPopups = ChatPopupService.getPopups(node)
    val kontaktChatPopupAktiv = selectedUser != null && chatPopups.any { popup ->
        popup.aktiv && popup.userName == selectedUser.id
    }
    val eigenerChatPopupAktiv = chatPopups.any { popup -> !popup.aktiv }
    val pageStateResetKey = remember(
        currentFirmenId,
        selectedCompanyId,
        selectedUserId,
        ownState?.id,
        ownState?.terminkalenderOnline,
        ownState?.terminkalenderIntern,
        ownState?.profilOnline,
        ownState?.profilIntern,
        ownState?.startbildschirm,
        selectedState?.id,
        selectedState?.terminkalenderOnline,
        selectedState?.terminkalenderIntern,
        selectedState?.profilOnline,
        selectedState?.profilIntern
    ) {
        listOf(
            currentFirmenId,
            selectedCompanyId,
            selectedUserId,
            ownState?.id.orEmpty(),
            ownState?.terminkalenderOnline?.toString().orEmpty(),
            ownState?.terminkalenderIntern?.toString().orEmpty(),
            ownState?.profilOnline?.toString().orEmpty(),
            ownState?.profilIntern?.toString().orEmpty(),
            ownState?.startbildschirm.orEmpty(),
            selectedState?.id.orEmpty(),
            selectedState?.terminkalenderOnline?.toString().orEmpty(),
            selectedState?.terminkalenderIntern?.toString().orEmpty(),
            selectedState?.profilOnline?.toString().orEmpty(),
            selectedState?.profilIntern?.toString().orEmpty()
        ).joinToString("#")
    }
    val profileTitle = ownState?.let { userDisplayName(it) }
        .orEmpty()
        .ifBlank { currentLoginUser?.let { userDisplayName(it) }.orEmpty() }
        .ifBlank { "Mein Profil" }
    val selectedCompanyLabel = companyOptions.firstOrNull { it.key == selectedCompanyId }?.label.orEmpty()
    val selectedUserLabel = selectedUser?.let { userDisplayName(it) }.orEmpty()
    val companyDropdownWidth = remember(selectedCompanyLabel) {
        dashboardProfilDropdownWidthPx(selectedCompanyLabel, "Firma")
    }
    val userDropdownWidth = remember(selectedUserLabel) {
        dashboardProfilDropdownWidthPx(selectedUserLabel, "User")
    }
    val contactSelectorWidth = companyDropdownWidth + userDropdownWidth + 12

    EngineKarteiScreen(
        entities = activeEntities,
        screenTitle = profileTitle,
        entityId = { it.id },
        entityLabel = { it.name.ifBlank { it.id } },
        loadState = { id -> activeEntities.firstOrNull { it.id == id } ?: All(id = id) },
        saveState = { state -> state.copy(aktiv = true) },
        anchorFields = emptyList(),
        pageScopedState = true,
        pageStateResetKey = pageStateResetKey,
        pageAnchorFields = mapOf(
            "page_1" to meinProfilAnchorFields,
            "page_2" to meinProfilAnchorFields
        ),
        loadPageState = { _, pageId ->
            when (pageId) {
                "page_1" -> ownState ?: All(id = currentUserId)
                "page_2" -> selectedState ?: All(id = selectedUserId)
                else -> null
            }
        },
        savePageState = { _, pageId, state ->
            when (pageId) {
                else -> state
            }
        },
        page1Label = "Mein Profil",
        page1Fields = meinProfilPageFields,
        page2Label = "Kontakte",
        page2Fields = meinProfilPageFields,
        showPhoto = true,
        staticPhotoSrc = "images/person.svg",
        showCreate = false,
        canCreate = false,
        showSave = false,
        selectorWidthPx = contactSelectorWidth,
        screenKey = "MeinProfilScreen",
        selectorContent = { activePageId ->
            Div({
                style {
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    gap(12.px)
                    minWidth(0.px)
                    flexWrap(FlexWrap.Nowrap)
                }
            }) {
                if (activePageId == "page_2") {
                    Div({ style { width(companyDropdownWidth.px); flexShrink(0) } }) {
                        AppDropdown(
                            options = companyOptions,
                            selectedValue = selectedCompanyId.takeIf { it.isNotBlank() },
                            onChange = { newCompanyId ->
                                if (newCompanyId.isNotBlank() && newCompanyId != selectedCompanyId) {
                                    selectedCompanyId = newCompanyId
                                    selectedUserId = ""
                                }
                            },
                            placeholder = "Firma",
                            attrs = {
                                style {
                                    height(34.px)
                                    fontSize(12.px)
                                    fontWeight("500")
                                    padding(0.px, 9.px)
                                }
                            }
                        )
                    }
                    Div({ style { width(userDropdownWidth.px); flexShrink(0) } }) {
                        AppDropdown(
                            options = usersForCompany.map { user ->
                                val userId = userChatId(user)
                                All(key = userId, label = userDisplayName(user))
                            },
                            selectedValue = selectedUserId.takeIf { it.isNotBlank() },
                            onChange = { newUserId ->
                                if (newUserId.isNotBlank()) selectedUserId = newUserId
                            },
                            placeholder = "User",
                            attrs = {
                                style {
                                    height(34.px)
                                    fontSize(12.px)
                                    fontWeight("500")
                                    padding(0.px, 9.px)
                                }
                            }
                        )
                    }
                } else {
                    DashboardProfilAccountNav(
                        onTwoFactor = {
                            showTrustedDevices = true
                            selectedTrustedDeviceIds = emptySet()
                            editingTrustedDeviceId = ""
                            editingTrustedDeviceLabel = ""
                            reloadTrustedDevices()
                        },
                        onPassword = { showPasswordOverlay = true },
                        onSettings = { showDashboardSettings = true },
                        onLogout = {
                            LoginService.logout()
                            window.location.reload()
                        }
                    )
                }
            }
        },
        actionContent = { activePageId ->
            val isKontaktPage = activePageId == "page_2"
            val chatButtonActive = if (isKontaktPage) kontaktChatPopupAktiv else eigenerChatPopupAktiv
            val kalenderButtonActive = if (isKontaktPage) kalenderPopupAktiv else eigenerKalenderPopupAktiv
            Div({
                style {
                    display(DisplayStyle.Flex)
                    alignItems(org.jetbrains.compose.web.css.AlignItems.Center)
                    gap(10.px)
                }
            }) {
                LumaticaButton(
                    label = "",
                    onClick = {
                        if (isKontaktPage) {
                            selectedUser?.let { user ->
                                ChatPopupService.openPopup(
                                    node = node,
                                    art = art,
                                    user = user,
                                    conversationOnly = true
                                )
                            }
                        } else {
                            ChatPopupService.openPopup(
                                node = node,
                                art = art,
                                user = null,
                                conversationOnly = false
                            )
                        }
                    },
                    variant = LumaticaButtonVariant.State,
                    enabled = if (isKontaktPage) canAct else true,
                    isActive = chatButtonActive,
                    heightPx = 40,
                    paddingH = 10,
                    content = {
                        Img(src = "images/chat.svg", attrs = {
                            style {
                                width(18.px)
                                height(18.px)
                            }
                        })
                    }
                )
                LumaticaButton(
                    label = "",
                    onClick = {
                        if (isKontaktPage) {
                            selectedUser?.let {
                                KontaktKalenderPopupService.openPopup(
                                    node = node,
                                    art = art,
                                    userId = chatIdentityUserId(it.id),
                                    firmenId = it.firmenId.trim(),
                                    label = shortUserDisplayName(it)
                                )
                            }
                        } else {
                            KontaktKalenderPopupService.openPopup(
                                node = node,
                                art = art,
                                userId = currentUserId,
                                firmenId = currentFirmenId,
                                label = shortUserDisplayName(currentLoginUser ?: All(vorname = "Ich", nachname = ""))
                            )
                        }
                    },
                    variant = LumaticaButtonVariant.State,
                    enabled = if (isKontaktPage) canOpenCalendar else true,
                    isActive = kalenderButtonActive,
                    heightPx = 40,
                    paddingH = 10,
                    content = {
                        Img(src = "images/kalender.svg", attrs = {
                            style {
                                width(18.px)
                                height(18.px)
                            }
                        })
                    }
                )
            }
        }
    )

    if (showPasswordOverlay) {
        PasswordChangeOverlay(
            art = art,
            onClose = { showPasswordOverlay = false }
        )
    }

    if (showDashboardSettings) {
        DashboardUserSettingsPopup(
            feedDefinitionsWithStatus = DashboardProfileSettingsBridge.feedDefinitionsWithStatus,
            userFeedPushEnabled = DashboardProfileSettingsBridge.userFeedPushEnabled,
            onFeedPushEnabledChange = { key, enabled ->
                DashboardProfileSettingsBridge.onFeedPushEnabledChange?.invoke(key, enabled)
            },
            onFeedSettingsSave = { next ->
                DashboardProfileSettingsBridge.onFeedSettingsSave?.invoke(next)
            },
            startScreenOptions = DashboardProfileSettingsBridge.startScreenOptions,
            selectedStartScreen = DashboardProfileSettingsBridge.selectedStartScreen,
            onStartScreenSave = { next ->
                DashboardProfileSettingsBridge.onStartScreenSave?.invoke(next)
            },
            onClose = { showDashboardSettings = false }
        )
    }

    if (showTrustedDevices) {
        PopupEngineCard(
            windowKey = "meinProfilTrustedDevices",
            widthPx = 460,
            heightPx = 430,
            minWidthPx = 420,
            minHeightPx = 260,
            initialX = (window.innerWidth.toDouble() - 500.0).coerceAtLeast(84.0),
            initialY = 120.0,
            contentBackground = DashboardTheme.POPUP_BG,
            frameBackground = null,
            resizable = true,
            viewportAnchored = true,
            onClose = { showTrustedDevices = false }
        ) {
            Div({
                style {
                    height(100.percent)
                    width(100.percent)
                    display(DisplayStyle.Flex)
                    flexDirection(FlexDirection.Column)
                    boxSizing("border-box")
                    padding(24.px)
                }
            }) {
                Span({
                    style {
                        color(DashboardTheme.TEXT_LIGHT)
                        fontSize(18.px)
                        fontWeight("800")
                        marginBottom(14.px)
                    }
                }) { Text("2F Browser-Freigaben") }
                DashboardTrustedDevicesPanel(
                    devices = trustedDevices,
                    selectedDeviceIds = selectedTrustedDeviceIds,
                    loading = loadingTrustedDevices,
                    onSelect = { device ->
                        selectedTrustedDeviceIds = if (device.id in selectedTrustedDeviceIds) {
                            selectedTrustedDeviceIds - device.id
                        } else {
                            selectedTrustedDeviceIds + device.id
                        }
                    },
                    editingDeviceId = editingTrustedDeviceId,
                    editingLabel = editingTrustedDeviceLabel,
                    onEdit = { device ->
                        editingTrustedDeviceId = device.id
                        editingTrustedDeviceLabel = device.label.ifBlank { "Browser-Freigabe" }
                    },
                    onEditLabel = { editingTrustedDeviceLabel = it },
                    onSaveLabel = {
                        val id = editingTrustedDeviceId
                        val label = editingTrustedDeviceLabel.trim()
                        if (id.isBlank() || label.isBlank()) return@DashboardTrustedDevicesPanel
                        loadingTrustedDevices = true
                        scope.launch {
                            if (TrustedDevicesApi.rename("desktopregistrierungscreen", id, label)) {
                                trustedDevices = TrustedDevicesApi.load("desktopregistrierungscreen")
                                editingTrustedDeviceId = ""
                                editingTrustedDeviceLabel = ""
                            }
                            loadingTrustedDevices = false
                        }
                    },
                    onCancelEdit = {
                        editingTrustedDeviceId = ""
                        editingTrustedDeviceLabel = ""
                    },
                    onCancel = {
                        selectedTrustedDeviceIds = emptySet()
                        editingTrustedDeviceId = ""
                        editingTrustedDeviceLabel = ""
                        showTrustedDevices = false
                    },
                    onDeleteSelected = {
                        val ids = selectedTrustedDeviceIds
                        if (ids.isEmpty()) return@DashboardTrustedDevicesPanel
                        loadingTrustedDevices = true
                        scope.launch {
                            ids.forEach { TrustedDevicesApi.delete("desktopregistrierungscreen", it) }
                            trustedDevices = TrustedDevicesApi.load("desktopregistrierungscreen")
                            selectedTrustedDeviceIds = emptySet()
                            loadingTrustedDevices = false
                        }
                    }
                )
            }
        }
    }

}

@Composable
private fun DashboardProfilAccountNav(
    onTwoFactor: () -> Unit,
    onPassword: () -> Unit,
    onSettings: () -> Unit,
    onLogout: () -> Unit
) {
    Div({
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            gap(10.px)
        }
    }) {
        MeinProfilAccountIcon("images/identity.svg", "2F ändern", onTwoFactor)
        MeinProfilAccountIcon("images/lock_reset.svg", "Passwort ändern", onPassword)
        MeinProfilAccountIcon("images/settings.svg", "Dashboard Einstellungen", onSettings)
        MeinProfilAccountIcon("images/logout.svg", "Logout", onLogout)
    }
}

@Composable
private fun MeinProfilAccountIcon(icon: String, title: String, onClick: () -> Unit) {
    Div({ attr("title", title) }) {
        LumaticaButton(
            label = "",
            onClick = onClick,
            variant = LumaticaButtonVariant.State,
            enabled = true,
            isActive = false,
            heightPx = 40,
            paddingH = 10,
            content = {
                Img(src = icon, attrs = {
                    style {
                        width(18.px)
                        height(18.px)
                    }
                })
            }
        )
    }
}

